import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";
import type { Customer } from "../lib/types.js";

export function CustomersList() {
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [search, setSearch] = useState("");

  async function load(q = "") {
    try {
      const path = q.trim() ? `/admin/customers?q=${encodeURIComponent(q.trim())}` : "/admin/customers";
      const res = await api.get<{ customers: Customer[] }>(path);
      setCustomers(res.customers);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load customers");
    }
  }

  // Debounced search: re-query as the admin types (name, email, licence code, or device name).
  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div>
      <div className="row-between">
        <h1>Customers</h1>
        <button className={showNewForm ? "secondary" : "primary"} onClick={() => setShowNewForm((v) => !v)}>{showNewForm ? "Cancel" : "New customer"}</button>
      </div>
      <input
        type="search"
        placeholder="Search by name, email, licence code, or device name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: "100%", maxWidth: 480, margin: "4px 0 8px" }}
        aria-label="Search customers"
      />
      {showNewForm && (
        <NewCustomerForm
          onCreated={() => {
            setShowNewForm(false);
            load(search);
          }}
        />
      )}
      {error && <p className="error-text">{error}</p>}
      {!customers && !error && <p>Loading…</p>}
      {customers && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Business</th>
              <th>Email</th>
              <th>Seats</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => {
              const seatsPurchased = c.seatsPurchased ?? 0;
              const seatsActive = c.seatsActive ?? 0;
              const overSeats = seatsActive > seatsPurchased;
              return (
                <tr key={c.id}>
                  <td>
                    <Link to={`/customers/${c.id}`}>{c.name}</Link>
                    {c.billingType === "po" && (
                      <span className="type-badge type-po" style={{ marginLeft: 8 }}>Invoice / PO</span>
                    )}
                  </td>
                  <td>{c.business_name ?? "—"}</td>
                  <td>{c.email}</td>
                  <td>
                    <span className={overSeats ? "text-danger" : undefined}>{seatsActive}</span> / {seatsPurchased}
                    {c.sharingFlag && (
                      <span className="sharing-badge" title="At least one licence is flagged for possible key sharing">
                        flagged
                      </span>
                    )}
                  </td>
                  <td>{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              );
            })}
            {customers.length === 0 && (
              <tr>
                <td colSpan={5}>{search.trim() ? "No customers match your search." : "No customers yet."}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewCustomerForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/admin/customers", { name, email, businessName: businessName || undefined });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create customer");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="inline-form">
      <input placeholder="Name" required value={name} onChange={(e) => setName(e.target.value)} />
      <input
        placeholder="Email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        placeholder="Business name (optional)"
        value={businessName}
        onChange={(e) => setBusinessName(e.target.value)}
      />
      <button className="primary" type="submit" disabled={submitting}>
        {submitting ? "Creating…" : "Create"}
      </button>
      {error && <p className="error-text">{error}</p>}
    </form>
  );
}
